import React, { useEffect, useState } from 'react';
import { Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { LocalOffer } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { useToast } from '../contexts/ToastContext';
import { InlineMessage, LoadingState, SectionCard } from './ui';
import {
  REBATE_TREATMENT_QUERY_KEY,
  RebateTreatmentChoice,
  saveRebateTreatment,
  useRebateTreatmentSetting,
} from './RebateTreatmentChoice';
import type { RebateCheckoutTreatment } from './RebateTreatmentChoice';

type TaxTreatment = 'pre_rebate' | 'post_rebate';
interface RebateTaxRuleSetting {
  override: TaxTreatment | null;
  effective: { treatment: TaxTreatment | 'unknown'; source: string; overridden: boolean; state: string | null };
}
const TAX_RULE_QUERY_KEY = ['store-rebate-tax-rule'] as const;
const FOLLOW_STATE = 'follow';

// Re-exported so existing importers of the card keep their paths.
export { REBATE_TREATMENT_QUERY_KEY };
export type { RebateCheckoutTreatment, RebateTreatmentSetting } from './RebateTreatmentChoice';

/**
 * Jordan chooses how a vendor instant rebate reaches the customer: off the
 * listed price, or as its own discount with the price left at MAP. There is no
 * safe default — the tax answer depends on the state — so the card stays
 * unanswered until the store picks one, and the choice drives every sync.
 */
export const RebateTreatmentCard: React.FC = () => {
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { enabled } = useOrgFeatureFlag('rebate_checkout_treatment');
  const [selected, setSelected] = useState<RebateCheckoutTreatment | ''>('');
  const { enabled: taxReportEnabled } = useOrgFeatureFlag('rebate_tax_report');
  const [taxSelected, setTaxSelected] = useState<string>(FOLLOW_STATE);

  // Whether any connected Shopify shop is on Plus (recorded on the integration, never asked).
  const integrationsQuery = useQuery({
    queryKey: ['integrations'],
    queryFn: async () => {
      const response = await makeApiCall('/integrations', { headers: await getAuthHeaders() });
      if (!response.ok) return [] as Array<{ platform?: string; isActive?: boolean; isShopifyPlus?: boolean | null }>;
      return ((await response.json()).data || []) as Array<{ platform?: string; isActive?: boolean; isShopifyPlus?: boolean | null }>;
    },
    enabled,
  });
  const shopifyIntegrations = (integrationsQuery.data || []).filter(
    (i) => i.platform === 'shopify' && i.isActive !== false,
  );
  const hasShopify = (integrationsQuery.isSuccess || integrationsQuery.isError) && shopifyIntegrations.length > 0;
  const shopifyPlus = shopifyIntegrations.some((i) => i.isShopifyPlus === true);

  const taxRule = useQuery({
    queryKey: TAX_RULE_QUERY_KEY,
    queryFn: async (): Promise<RebateTaxRuleSetting> => {
      const response = await makeApiCall('/store/settings/rebate-tax-rule', { headers: await getAuthHeaders() });
      const body = await response.json().catch(() => ({}));
      if (!response.ok || !body?.success) throw new Error(body?.error?.message || body?.message || t('store.rebateTax.loadError'));
      return body.data as RebateTaxRuleSetting;
    },
    enabled: enabled && taxReportEnabled,
  });
  useEffect(() => {
    if (taxRule.data) setTaxSelected(taxRule.data.override ?? FOLLOW_STATE);
  }, [taxRule.data]);

  const saveTax = useMutation({
    mutationFn: async (value: string) => {
      const response = await makeApiCall('/store/settings/rebate-tax-rule', {
        method: 'PUT',
        headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
        body: JSON.stringify({ treatment: value === FOLLOW_STATE ? null : value }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok || !body?.success) throw new Error(body?.error?.message || body?.message || t('store.rebateTax.saveError'));
      return body.data as RebateTaxRuleSetting;
    },
    onSuccess: () => {
      showToast(t('store.rebateTax.saved'), 'success');
      queryClient.invalidateQueries({ queryKey: TAX_RULE_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['rebate-report'] });
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });

  const { data, isLoading, isError } = useRebateTreatmentSetting(enabled);

  useEffect(() => {
    if (data?.treatment) setSelected(data.treatment);
  }, [data?.treatment]);

  const save = useMutation({
    mutationFn: (treatment: RebateCheckoutTreatment) =>
      saveRebateTreatment(treatment, t('store.rebateTreatment.saveError')),
    onSuccess: () => {
      showToast(t('store.rebateTreatment.saved'), 'success');
      queryClient.invalidateQueries({ queryKey: REBATE_TREATMENT_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['store-rebate-programs'] });
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });

  if (!enabled) return null;

  return (
    <SectionCard title={t('store.rebateTreatment.title')} leading={<LocalOffer color="primary" />}>
      {isLoading ? (
        <LoadingState />
      ) : isError || !data ? (
        <InlineMessage severity="error">{t('store.rebateTreatment.loadError')}</InlineMessage>
      ) : (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t('store.rebateTreatment.intro')}
          </Typography>
          {!data.chosen && (
            <InlineMessage severity="info">{t('store.rebateTreatment.prompt')}</InlineMessage>
          )}
          <RebateTreatmentChoice
            setting={data}
            value={selected}
            onChange={setSelected}
            hasShopify={hasShopify}
            shopifyPlus={shopifyPlus}
          />
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              size="small"
              disabled={!selected || selected === data.treatment || save.isPending}
              onClick={() => selected && save.mutate(selected)}
            >
              {t('common.save')}
            </Button>
          </Stack>
          {taxReportEnabled && taxRule.data && (
            <Stack spacing={1} data-testid="rebate-tax-rule">
              <Typography variant="subtitle2">{t('store.rebateTax.title')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('store.rebateTax.help')}
              </Typography>
              <TextField
                select
                size="small"
                value={taxSelected}
                onChange={(event) => setTaxSelected(event.target.value)}
                inputProps={{ 'aria-label': t('store.rebateTax.title') }}
                sx={{ maxWidth: 420 }}
              >
                <MenuItem value={FOLLOW_STATE}>
                  {taxRule.data.effective.state && !taxRule.data.effective.overridden
                    ? t('store.rebateTax.followState', {
                        state: taxRule.data.effective.state,
                        treatment: t(
                          taxRule.data.effective.treatment === 'pre_rebate'
                            ? 'store.rebateTax.treatmentPre'
                            : taxRule.data.effective.treatment === 'post_rebate'
                              ? 'store.rebateTax.treatmentPost'
                              : 'store.rebateTax.unknown',
                        ),
                      })
                    : t('store.rebateTax.follow')}
                </MenuItem>
                <MenuItem value="pre_rebate">{t('store.rebateTax.pre_rebate')}</MenuItem>
                <MenuItem value="post_rebate">{t('store.rebateTax.post_rebate')}</MenuItem>
              </TextField>
              {!taxRule.data.effective.state && !taxRule.data.override && (
                <Typography variant="caption" color="text.secondary">
                  {t('store.rebateTax.noState')}
                </Typography>
              )}
              {taxRule.data.effective.source && !taxRule.data.effective.overridden && (
                <Typography variant="caption" color="text.secondary">
                  {t('store.rebateTax.source', { source: taxRule.data.effective.source })}
                </Typography>
              )}
              <Stack direction="row">
                <Button
                  variant="outlined"
                  size="small"
                  disabled={taxSelected === (taxRule.data.override ?? FOLLOW_STATE) || saveTax.isPending}
                  onClick={() => saveTax.mutate(taxSelected)}
                >
                  {t('common.save')}
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      )}
    </SectionCard>
  );
};

export default RebateTreatmentCard;
